import type { ErrorResponse, RefreshTokenResponse } from './types'
import { requestAuthModal } from '../lib/authModalBridge'

/**
 * Where the session lives, and why none of it is in `localStorage`.
 *
 * The access token, the participant id, and the roles are held in module scope —
 * memory only, gone on reload — because CLAUDE.md Rule 14 forbids persisting
 * credentials to storage a script can read. The refresh token never reaches this
 * file at all: the server sets it as an `httpOnly` cookie scoped to `/api/v1/auth`,
 * so JavaScript cannot read it even in principle, and an XSS can no longer walk off
 * with a credential that outlives the page (design/40 §4 P3, DEF-31).
 *
 * The cost is that a reload starts with no access token, which is what
 * `bootstrapSession` exists to fix.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''
const USE_PROXY_API =
  API_BASE_URL === '' || /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/?$/i.test(API_BASE_URL)

export function apiUrl(path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  if (USE_PROXY_API) return normalizedPath
  return new URL(normalizedPath, API_BASE_URL).toString()
}

export class ApiError extends Error {
  status: number
  body: ErrorResponse

  constructor(status: number, body: ErrorResponse) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/** A request that could not establish whether the server received it. */
export class ApiConnectivityError extends Error {
  constructor(message = 'Could not reach the server. Please try again.') {
    super(message)
    this.name = 'ApiConnectivityError'
  }
}

let sessionToken: string | null = null
let sessionParticipantId: string | null = null
let sessionRoles: string[] = []
let sessionActiveRole: string | null = null

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

const DEVICE_ID_STORAGE_KEY = 'osle.web.device_id'

/**
 * Stable per-browser device identifier. Persisted across logins so the server's
 * new-device login alert only fires when this changes (new browser/computer,
 * incognito, or cleared storage) — not on every sign-in. Browsers cannot read a
 * hardware MAC address; this UUID is the privacy-safe equivalent. Survives
 * logout (clearSession does not remove it).
 */
export function getDeviceId(): string {
  if (!canUseStorage()) return crypto.randomUUID()
  let id = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY)
  if (!id) {
    id = crypto.randomUUID()
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, id)
  }
  return id
}

export function setSession(
  token: string,
  participantId: string,
  roles: string[],
  activeRole?: string | null,
) {
  sessionToken = token
  sessionParticipantId = participantId
  sessionRoles = roles
  if (activeRole !== undefined) {
    sessionActiveRole = activeRole
  }
}

export function clearSession() {
  sessionToken = null
  sessionParticipantId = null
  sessionRoles = []
  sessionActiveRole = null
}

export function getSessionToken() {
  return sessionToken
}

export function getParticipantId() {
  return sessionParticipantId
}

export function getSessionRoles() {
  return sessionRoles
}

export function setActiveSessionRole(role: string | null) {
  sessionActiveRole = role
}

export function isLoggedIn() {
  return sessionToken !== null
}

/** Read `sub` and `roles` out of an access token without verifying it.
 *
 * Identity for rendering only — the server re-derives both from the signed token on
 * every request, so a tampered payload buys nothing. This is what lets a reload
 * recover who the user is from the refreshed token alone, with no extra round trip.
 */
function identityFromAccessToken(token: string): { sub: string; roles: string[] } | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(json) as { sub?: unknown; roles?: unknown }
    if (typeof claims.sub !== 'string') return null
    return {
      sub: claims.sub,
      roles: Array.isArray(claims.roles) ? (claims.roles as string[]) : [],
    }
  } catch {
    return null
  }
}

/**
 * What a refresh attempt actually established.
 *
 * `no-session` and `unreachable` both leave the user without a token, but they mean
 * opposite things and must not be handled alike: the first is the server's verdict
 * that the session is over, the second is that we never got a verdict. Collapsing
 * them into one boolean is how a momentary loss of signal used to look identical to
 * a logout — which on this network is the common case, not the rare one.
 */
export type RefreshOutcome = 'restored' | 'no-session' | 'unreachable'

/**
 * Swap the refresh cookie for a new access token.
 *
 * Sends no body: the cookie is the credential, and `credentials: 'include'` is what
 * puts it on the request. The server rotates the token on every call and sets the
 * replacement cookie on the response.
 */
async function requestRefresh(): Promise<RefreshOutcome> {
  let res: Response
  try {
    res = await fetch(apiUrl('api/v1/auth/refresh-token'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
  } catch {
    // `fetch` rejects only on network failure — DNS, offline, connection reset.
    return 'unreachable'
  }

  // 5xx is the server failing to answer, not a ruling on the session. Treating a
  // deploy or a restart as "you are logged out" would sign out every open tab.
  if (res.status >= 500) return 'unreachable'
  if (!res.ok) return 'no-session'

  try {
    const data = (await res.json()) as RefreshTokenResponse
    sessionToken = data.access_token
    const identity = identityFromAccessToken(data.access_token)
    if (identity) {
      sessionParticipantId = identity.sub
      // Only seed roles when we have none — a live session's roles are kept current
      // by `syncParticipant`, and the token's copy is a snapshot from issue time.
      if (sessionRoles.length === 0) sessionRoles = identity.roles
    }
    return 'restored'
  } catch {
    return 'unreachable'
  }
}

let refreshInFlight: Promise<RefreshOutcome> | null = null

/** Share a refresh across requests that discover an expired access token together. */
function refreshSession(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = requestRefresh().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

const BOOTSTRAP_RETRY_MS = 1_000

/**
 * Restore a session on page load.
 *
 * Nothing survives a reload in memory, so the app starts signed out and asks the
 * refresh cookie whether it still has a session. One retry covers a blip without
 * making the caller stare at a spinner; anything longer is left to the caller, which
 * can wait for the browser to report it is back online instead of blocking the boot.
 */
export async function bootstrapSession(retries = 1): Promise<RefreshOutcome> {
  for (let attempt = 0; ; attempt += 1) {
    const outcome = await refreshSession()
    if (outcome !== 'unreachable' || attempt >= retries) return outcome
    await new Promise((resolve) => setTimeout(resolve, BOOTSTRAP_RETRY_MS))
  }
}

export interface RequestOptions {
  method?: string
  body?: unknown
  /**
   * Query parameters. An array becomes a repeated parameter (`?id=a&id=b`), which is
   * what FastAPI parses into a `list[...]` — comma-joining would arrive as one value.
   */
  params?: Record<string, string | number | string[] | undefined>
  jsonReviver?: (key: string, value: unknown) => unknown
  cache?: RequestCache
  headers?: Record<string, string>
  /** Abort from the caller, for example when a page unmounts. */
  signal?: AbortSignal
  /** Upper bound for a request that would otherwise wait indefinitely. */
  timeoutMs?: number
}

const DEFAULT_REQUEST_TIMEOUT_MS = 20_000

async function fetchWithDeadline(
  input: RequestInfo | URL,
  init: RequestInit,
  signal?: AbortSignal,
  timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  const abortFromCaller = () => controller.abort(signal?.reason)
  signal?.addEventListener('abort', abortFromCaller, { once: true })
  if (signal?.aborted) abortFromCaller()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } catch (error) {
    if (controller.signal.aborted && !signal?.aborted) {
      throw new ApiConnectivityError('The request timed out. Please try again.')
    }
    throw error
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', abortFromCaller)
  }
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET', body, params, jsonReviver, cache, headers: extraHeaders, signal, timeoutMs,
  } = options

  const url = apiUrl(path)
  const baseOrigin = window.location?.origin || 'http://localhost'
  const urlObject = new URL(url, baseOrigin)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined) continue
      if (Array.isArray(value)) {
        for (const entry of value) urlObject.searchParams.append(key, entry)
      } else {
        urlObject.searchParams.set(key, String(value))
      }
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  }

  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`
  }
  if (sessionActiveRole) {
    headers['X-OSLE-Active-Role'] = sessionActiveRole
  }

  let response = await fetchWithDeadline(urlObject.toString(), {
    method,
    headers,
    cache,
    body: body ? JSON.stringify(body) : undefined,
  }, signal, timeoutMs)

  if (response.status === 401) {
    const refreshOutcome = await refreshSession()
    if (refreshOutcome === 'restored') {
      headers['Authorization'] = `Bearer ${sessionToken}`
      if (sessionActiveRole) {
        headers['X-OSLE-Active-Role'] = sessionActiveRole
      }
      response = await fetchWithDeadline(urlObject.toString(), {
        method,
        headers,
        cache,
        body: body ? JSON.stringify(body) : undefined,
      }, signal, timeoutMs)
    } else if (refreshOutcome === 'unreachable') {
      // We have no verdict that the refresh cookie is invalid. Keep the live
      // access token and let the caller offer a retry instead of logging out.
      throw new ApiConnectivityError('Could not refresh your session. Please try again.')
    } else {
      // Only an *expired* session earns a prompt. A 401 with no session left
      // means either the caller was anonymous or the user signed out while this
      // request was in flight — and after a deliberate sign-out the queries still
      // mounted on the page each 401 in turn, popping sign-in over the public
      // page the user just landed on.
      const sessionExpired = sessionToken !== null
      clearSession()
      if (sessionExpired) {
        // Popup over the current page rather than a reload onto /login — the
        // provider clears auth state and prompts (see lib/authModalBridge).
        requestAuthModal()
      }
      throw new Error('Session expired')
    }
  }

  if (!response.ok) {
    let errorBody: ErrorResponse
    try {
      errorBody = await response.json()
    } catch {
      errorBody = { error_code: 'unknown', message: `HTTP ${response.status}` }
    }
    throw new ApiError(response.status, errorBody)
  }

  if (jsonReviver) {
    const text = await response.text()
    return JSON.parse(text, jsonReviver) as T
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

export async function apiFetchBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const headers: Record<string, string> = {}
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`
  if (sessionActiveRole) headers['X-OSLE-Active-Role'] = sessionActiveRole

  let response = await fetchWithDeadline(
    apiUrl(path), { headers, cache: 'no-store' }, options.signal, options.timeoutMs,
  )
  if (response.status === 401 && (await refreshSession()) === 'restored') {
    headers.Authorization = `Bearer ${sessionToken}`
    response = await fetchWithDeadline(
      apiUrl(path), { headers, cache: 'no-store' }, options.signal, options.timeoutMs,
    )
  }
  if (!response.ok) {
    throw new Error(`Could not load protected image (HTTP ${response.status}).`)
  }
  return response.blob()
}

export async function apiUploadForm<T>(
  path: string, body: FormData, options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {}
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`
  if (sessionActiveRole) headers['X-OSLE-Active-Role'] = sessionActiveRole

  let response = await fetchWithDeadline(
    apiUrl(path), { method: 'POST', headers, body }, options.signal, options.timeoutMs,
  )
  if (response.status === 401 && (await refreshSession()) === 'restored') {
    headers.Authorization = `Bearer ${sessionToken}`
    response = await fetchWithDeadline(
      apiUrl(path), { method: 'POST', headers, body }, options.signal, options.timeoutMs,
    )
  }
  if (!response.ok) {
    throw new Error(`Upload failed (HTTP ${response.status}).`)
  }
  return response.json() as Promise<T>
}
